import clsx from 'clsx';
import * as React from 'react';

import { Spinner } from '@/components/Elements/Spinner';

const variants = {
  primary: 'bg-royal-blue-500 text-neutral-000 hover:bg-royal-blue-600',
  inverse: 'bg-neutral-000 text-royal-blue-500 hover:bg-royal-blue-500 hover:text-neutral-000',
  'primary-text': 'bg-neutral-000 text-royal-blue-500 hover:bg-neutral-300',
  danger: 'bg-danger-500 text-neutral-000 hover:bg-danger-400',
  'text-only':
    'border-none transition-all text-neutral-900 shadow-none hover:bg-royal-blue-500 hover:text-neutral-000 p-2',
};

const sizes = {
  unset: '',
  sm: 'py-2 px-4 text-sm',
  md: 'py-2  px-6 text-md',
  lg: 'py-3 px-8 text-lg',
  'md-mobile': 'py-4 px-6 text-lg sm:py-2 sm:px-6 sm:text-md',
};

type IconProps =
  | { startIcon: React.ReactElement; endIcon?: never }
  | { endIcon: React.ReactElement; startIcon?: never }
  | { endIcon?: undefined; startIcon?: undefined };

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  isLoading?: boolean;
} & IconProps;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className = '',
      variant = 'primary',
      size = 'md',
      isLoading = false,
      startIcon,
      endIcon,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={clsx(
          'flex justify-center items-center border border-neutral-400 transition-all disabled:opacity-70 disabled:cursor-not-allowed rounded-md shadow-sm font-medium focus:outline-none',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {isLoading && <Spinner size="sm" className="text-current" />}
        {!isLoading && startIcon}
        <span className="mx-2">{props.children}</span> {!isLoading && endIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
